/**
 * TransactionFilterModal
 *
 * 거래 필터 모달 (개선된 UI)
 * - 거래 타입 (전체/수입/지출)
 * - 카테고리 (지출/수입 구분, 칩 스타일, 전체 버튼)
 * - 결제수단 (사람별 구분, 칩 스타일, 전체 버튼)
 * - 지출자 (칩 스타일, 전체 버튼)
 * - 기간 (시작일/종료일)
 */

import { useState, useMemo, useEffect } from 'react'
import type { TransactionFilter, TransactionType} from '../types/Transaction'
import { Modal } from '@/shared/components/ui/Modal'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useCategories } from '@/domains/category/hooks/useCategories'

/**
 * 테마 색상 정의
 * 나중에 전역 테마로 추출 가능
 */
const THEME_COLORS = {
  // 선택된 상태 (Primary)
  selected: {
    background: 'bg-primary-50 dark:bg-primary-900/30',
    text: 'text-primary-700 dark:text-primary-300',
    border: 'border-primary-500 dark:border-primary-400',
    hover: '', // 선택된 상태에서는 hover 효과 없음
  },
  // 선택 안된 상태 (Default)
  unselected: {
    background: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-200',
    border: 'border-gray-300 dark:border-gray-600',
    hover: 'hover:bg-gray-200 dark:hover:bg-gray-600',
  },
  // 강조 색상 (Accent)
  accent: {
    background: 'bg-primary-600',
    text: 'text-white',
    hover: 'hover:bg-primary-700',
  },
} as const

interface TransactionFilterModalProps {
  isOpen: boolean
  currentFilter: TransactionFilter
  onApply: (filter: TransactionFilter) => void
  onClose: () => void
}

/**
 * SelectableChip - 선택 가능한 칩 버튼 컴포넌트
 */
interface SelectableChipProps {
  label: string
  icon?: string
  selected: boolean
  onClick: () => void
}

function SelectableChip({ label, icon, selected, onClick }: SelectableChipProps) {
  const colors = selected ? THEME_COLORS.selected : THEME_COLORS.unselected

  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 min-h-12 rounded-full text-sm font-medium transition-colors ${colors.background} ${colors.text} ${!selected && colors.hover}`}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {label}
    </button>
  )
}

/**
 * SectionHeader - 섹션 헤더 (타이틀 + 전체 버튼)
 */
interface SectionHeaderProps {
  title: string
  count?: number
  onSelectAll?: () => void
  allSelected?: boolean
}

function SectionHeader({ title, count, onSelectAll, allSelected }: SectionHeaderProps) {
  // 아무것도 선택 안됨(count === 0) 또는 모두 선택됨(allSelected) 일 때 파란색
  const isAllActive = count === 0 || allSelected
  const colors = isAllActive ? THEME_COLORS.selected : THEME_COLORS.unselected

  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
        {title} {count !== undefined && count > 0 && `(${count})`}
      </h3>
      {onSelectAll && (
        <button
          onClick={onSelectAll}
          className={`px-4 py-3 min-h-12 rounded-full text-sm font-medium transition-colors ${colors.background} ${colors.text} ${!isAllActive && colors.hover}`}
        >
          전체
        </button>
      )}
    </div>
  )
}

export function TransactionFilterModal({
  isOpen,
  currentFilter,
  onApply,
  onClose,
}: TransactionFilterModalProps) {
  // Fetch real data
  const { users, loading: usersLoading, error: usersError } = useUsers()
  const { paymentMethods, loading: paymentMethodsLoading, error: pmError } = usePaymentMethods()
  const { expenseCategories, incomeCategories, loading: categoriesLoading, error: categoriesError } = useCategories()

  // 로컬 필터 상태
  const [filterType, setFilterType] = useState<TransactionType | 'all'>(
    currentFilter.type || 'all'
  )
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    currentFilter.majorCategoryIds || (currentFilter.majorCategoryId ? [currentFilter.majorCategoryId] : [])
  )
  const [selectedPaymentMethodIds, setSelectedPaymentMethodIds] = useState<string[]>(
    currentFilter.paymentMethodIds || (currentFilter.paymentMethodId ? [currentFilter.paymentMethodId] : [])
  )
  const [selectedSpenderIds, setSelectedSpenderIds] = useState<string[]>(
    currentFilter.spenderIds || (currentFilter.spenderId ? [currentFilter.spenderId] : [])
  )
  const [startDate, setStartDate] = useState(currentFilter.startDate || '')
  const [endDate, setEndDate] = useState(currentFilter.endDate || '')

  // 전체 ID 목록
  const allCategoryIds = useMemo(() => {
    return [...expenseCategories, ...incomeCategories].map(c => c.id)
  }, [])

  const allPaymentMethodIds = useMemo(() => {
    return paymentMethods.map(pm => pm.id)
  }, [paymentMethods])

  const allSpenderIds = useMemo(() => {
    return users.map(u => u.id)
  }, [users])

  // 결제수단을 사람별로 그룹화
  const paymentMethodsByUser = useMemo(() => {
    const grouped = new Map<string, typeof paymentMethods>()
    users.forEach(user => {
      const userPaymentMethods = paymentMethods.filter(pm => pm.ownerId === user.id)
      if (userPaymentMethods.length > 0) {
        grouped.set(user.id, userPaymentMethods)
      }
    })
    return grouped
  }, [users, paymentMethods])

  // 토글 핸들러
  const toggleSelection = (id: string, currentList: string[], setter: (list: string[]) => void) => {
    if (currentList.includes(id)) {
      setter(currentList.filter(item => item !== id))
    } else {
      setter([...currentList, id])
    }
  }

  // 전체 선택/해제 핸들러
  const handleSelectAll = (allIds: string[], currentSelected: string[], setter: (ids: string[]) => void) => {
    if (currentSelected.length === allIds.length) {
      setter([])
    } else {
      setter(allIds)
    }
  }

  // 초기화
  const handleReset = () => {
    setFilterType('all')
    setSelectedCategoryIds([])
    setSelectedPaymentMethodIds([])
    setSelectedSpenderIds([])
    setStartDate('')
    setEndDate('')
  }

  // 변경 개수 계산
  const changeCount = useMemo(() => {
    let count = 0
    if (filterType !== 'all') count++
    if (selectedCategoryIds.length > 0) count++
    if (selectedPaymentMethodIds.length > 0) count++
    if (selectedSpenderIds.length > 0) count++
    if (startDate || endDate) count++
    return count
  }, [filterType, selectedCategoryIds, selectedPaymentMethodIds, selectedSpenderIds, startDate, endDate])

  // 적용
  const handleApply = () => {
    const newFilter: TransactionFilter = {}

    if (filterType !== 'all') {
      newFilter.type = filterType
    }

    if (selectedCategoryIds.length > 0) {
      newFilter.majorCategoryIds = selectedCategoryIds
    }

    if (selectedPaymentMethodIds.length > 0) {
      newFilter.paymentMethodIds = selectedPaymentMethodIds
    }

    if (selectedSpenderIds.length > 0) {
      newFilter.spenderIds = selectedSpenderIds
    }

    if (startDate) {
      newFilter.startDate = startDate
    }
    if (endDate) {
      newFilter.endDate = endDate
    }

    onApply(newFilter)
    onClose()
  }

  // Enter 키로 적용
  useEffect(() => {
    if (!isOpen) return

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLElement) {
        // date input이나 다른 입력 필드에서는 Enter 무시
        if (e.target.tagName === 'INPUT' && e.target.getAttribute('type') === 'date') {
          return
        }
        e.preventDefault()
        handleApply()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [isOpen, handleApply])

  // Loading state
  if (usersLoading || paymentMethodsLoading || categoriesLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="거래 필터" size="lg">
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500 dark:text-gray-400">데이터 로딩 중...</div>
        </div>
      </Modal>
    )
  }

  // Error state
  if (categoriesError || pmError || usersError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="거래 필터" size="lg">
        <div className="flex items-center justify-center py-8">
          <div className="text-red-500 text-center">
            <div className="text-4xl mb-2">⚠️</div>
            <p>필터 데이터를 불러오는데 실패했습니다</p>
            {categoriesError && <p className="text-sm mt-2 text-gray-600 dark:text-gray-400">카테고리: {categoriesError.message}</p>}
            {pmError && <p className="text-sm mt-2 text-gray-600 dark:text-gray-400">결제수단: {pmError.message}</p>}
            {usersError && <p className="text-sm mt-2 text-gray-600 dark:text-gray-400">사용자: {usersError.message}</p>}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="거래 필터" size="lg">
      {/* 스크롤 가능 영역 */}
      <div className="space-y-6 pb-20">
        {/* 1. 거래 타입 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">거래 타입</h3>
          <div className="flex gap-2">
            {[
              { value: 'all' as const, label: '전체' },
              { value: 'income' as const, label: '수입' },
              { value: 'expense' as const, label: '지출' },
            ].map(({ value, label }) => {
              const isSelected = filterType === value
              const colors = isSelected ? THEME_COLORS.selected : THEME_COLORS.unselected

              return (
                <label
                  key={value}
                  className={`px-4 py-3 min-h-12 flex items-center justify-center rounded-full text-sm font-medium cursor-pointer transition-colors ${colors.background} ${colors.text} ${!isSelected && colors.hover}`}
                >
                  <input
                    type="radio"
                    name="filterType"
                    value={value}
                    checked={isSelected}
                    onChange={() => setFilterType(value)}
                    className="sr-only"
                  />
                  {label}
                </label>
              )
            })}
          </div>
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-200 dark:border-gray-700 -mx-6" />

        {/* 2. 카테고리 (지출/수입 구분) */}
        <div>
          <SectionHeader
            title="카테고리"
            count={selectedCategoryIds.length}
            onSelectAll={() => handleSelectAll(allCategoryIds, selectedCategoryIds, setSelectedCategoryIds)}
            allSelected={selectedCategoryIds.length === allCategoryIds.length}
          />

          {/* 지출 카테고리 */}
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">지출</div>
            <div className="flex flex-wrap gap-2">
              {expenseCategories.map((category) => (
                <SelectableChip
                  key={category.id}
                  label={category.name}
                  icon={category.icon}
                  selected={selectedCategoryIds.includes(category.id)}
                  onClick={() => toggleSelection(category.id, selectedCategoryIds, setSelectedCategoryIds)}
                />
              ))}
            </div>
          </div>

          {/* 수입 카테고리 */}
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">수입</div>
            <div className="flex flex-wrap gap-2">
              {incomeCategories.map((category) => (
                <SelectableChip
                  key={category.id}
                  label={category.name}
                  icon={category.icon}
                  selected={selectedCategoryIds.includes(category.id)}
                  onClick={() => toggleSelection(category.id, selectedCategoryIds, setSelectedCategoryIds)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-200 dark:border-gray-700 -mx-6" />

        {/* 3. 결제수단 (사람별 구분) */}
        <div>
          <SectionHeader
            title="결제수단"
            count={selectedPaymentMethodIds.length}
            onSelectAll={() => handleSelectAll(allPaymentMethodIds, selectedPaymentMethodIds, setSelectedPaymentMethodIds)}
            allSelected={selectedPaymentMethodIds.length === allPaymentMethodIds.length}
          />

          {users.map((user) => {
            const userPaymentMethods = paymentMethodsByUser.get(user.id)
            if (!userPaymentMethods) return null

            return (
              <div key={user.id} className="mb-4 last:mb-0">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">{user.name}</div>
                <div className="flex flex-wrap gap-2">
                  {userPaymentMethods.map((pm) => (
                    <SelectableChip
                      key={pm.id}
                      label={pm.name}
                      selected={selectedPaymentMethodIds.includes(pm.id)}
                      onClick={() => toggleSelection(pm.id, selectedPaymentMethodIds, setSelectedPaymentMethodIds)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-200 dark:border-gray-700 -mx-6" />

        {/* 4. 결제 계좌 소유자 */}
        <div>
          <SectionHeader
            title="결제 계좌 소유자"
            count={selectedSpenderIds.length}
            onSelectAll={() => handleSelectAll(allSpenderIds, selectedSpenderIds, setSelectedSpenderIds)}
            allSelected={selectedSpenderIds.length === allSpenderIds.length}
          />

          <div className="flex gap-2">
            {users.map((user) => (
              <SelectableChip
                key={user.id}
                label={user.name}
                selected={selectedSpenderIds.includes(user.id)}
                onClick={() => toggleSelection(user.id, selectedSpenderIds, setSelectedSpenderIds)}
              />
            ))}
          </div>
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-200 dark:border-gray-700 -mx-6" />

        {/* 5. 기간 */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">기간</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                시작일
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                종료일
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

      </div>

      {/* Sticky 버튼 영역 */}
      <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 -mx-6 -mb-4 shadow-lg">
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            초기화
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            적용{changeCount > 0 && ` (${changeCount})`}
          </button>
        </div>
      </div>
    </Modal>
  )
}
